// Determine environment from hostname
import logger from "@/utils/logger";

const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname.includes("prod") || hostname === "lexi.tips") {
    return "prod";
  }
  if (hostname.includes("staging")) {
    return "staging";
  }
  // Default to dev for localhost and any other domains
  return "dev";
};

const environment = getEnvironment();
let API_BASE_URL;
if (
  environment === "dev" ||
  environment === "staging" ||
  environment === "prod"
) {
  API_BASE_URL = `https://lexi-${environment}-lexi-1-mcdr62tc5a-ts.a.run.app`;
} else {
  API_BASE_URL = `https://australia-southeast1-staible-returns.cloudfunctions.net/staible-returns-${environment}-lexi-1`;
}

export async function sendChatMessage(messages, userId, context) {
  try {
    const userMessage = messages[messages.length - 1];

    // Add detailed debugging for the track value
    // logger.log("DEBUG - Track value:", context?.track);
    // logger.log("DEBUG - Track type:", typeof context?.track);
    // logger.log("DEBUG - Track value is undefined:", context?.track === undefined);
    // logger.log("DEBUG - Track value is null:", context?.track === null);
    // logger.log("DEBUG - Track value is empty string:", context?.track === "");
    // logger.log("DEBUG - Context object:", JSON.stringify(context, null, 2));

    // Create a consistent contextMarker by combining track and race
    // Format track name by removing spaces and making it consistent
    const formattedTrack = context?.track
      ? context.track.replace(/\s+/g, "-").toLowerCase()
      : "";
    const raceNumber = context?.race || "";
    const contextMarker =
      formattedTrack && raceNumber
        ? `${formattedTrack}-race-${raceNumber}`
        : "";

    // logger.log("DEBUG - Created contextMarker:", contextMarker);

    // logger.log("Sending context to API:", {
    //   track: context?.track,
    //   race: context?.race,
    //   message: userMessage.content,
    //   environment,
    //   contextMarker,
    // });

    // Create request body
    const requestBody = {
      userId: userId || "anonymous-user",
      message: userMessage.content,
      date: context?.date,
      hasHistoricalData: context?.hasHistoricalData,
      analysis: context?.analysis,
      track: context?.track,
      trackName: context?.track,
      trackname: context?.track,
      TRACK: context?.track,
      race: context?.race,
      raceNumber: context?.race,
      contextMarker: contextMarker,
      stream: true, // Enable streaming
    };

    // logger.log("DEBUG - Full request body:", JSON.stringify(requestBody, null, 2));

    logger.log("Sending request to backend:", {
      url: API_BASE_URL,
      stream: requestBody.stream,
      userId: requestBody.userId,
      message: requestBody.message.substring(0, 50) + "...", // Log truncated message for privacy
    });

    // For streaming responses
    if (requestBody.stream) {
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Origin: window.location.origin,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error("API Error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(
          `API Error: ${response.status} - ${errorText || response.statusText}`,
        );
      }

      logger.log(
        "Stream response received, headers:",
        Object.fromEntries(response.headers.entries()),
      );

      // Check if we have a body to read from
      if (response.body) {
        logger.log("Stream body is available, returning for processing");

        // Check response headers to see if this is actually a streaming response
        const contentType = response.headers.get("content-type");
        if (
          contentType &&
          (contentType.includes("text/event-stream") ||
            contentType.includes("application/octet-stream") ||
            contentType.includes("application/json"))
        ) {
          return response.body;
        } else {
          logger.warn(
            "Response doesn't appear to be a stream based on content-type:",
            contentType,
          );
          // Try to handle as JSON anyway
          try {
            const jsonResponse = await response.json();
            logger.log("Non-stream JSON response:", jsonResponse);
            return {
              role: "assistant",
              content:
                jsonResponse.message ||
                jsonResponse.response ||
                "I received your message but couldn't generate a proper response.",
            };
          } catch (parseError) {
            // If we can't parse as JSON, get the text
            const textResponse = await response.text();
            logger.log("Non-stream text response:", textResponse);
            return {
              role: "assistant",
              content:
                textResponse ||
                "I received your message but couldn't generate a proper response.",
            };
          }
        }
      } else {
        logger.error("No response body available for streaming");
        // Fall back to regular JSON response handling
        const jsonResponse = await response.json().catch((e) => {
          logger.error("Could not parse response as JSON:", e);
          return { message: "Error: Could not parse response" };
        });

        logger.log("Falling back to JSON response:", jsonResponse);
        return {
          role: "assistant",
          content:
            jsonResponse.message ||
            jsonResponse.response ||
            "I received your message but couldn't generate a proper response.",
        };
      }
    }
    // For non-streaming responses (keeping for backwards compatibility)
    else {
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Origin: window.location.origin,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error("API Error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          url: API_BASE_URL,
          environment,
        });
        throw new Error(
          `API Error: ${response.status} - ${errorText || response.statusText}`,
        );
      }

      const data = await response.json();

      if (!data || (!data.message && !data.response)) {
        logger.error("Invalid API Response:", data);
        throw new Error("Invalid response format from API");
      }

      return {
        role: "assistant",
        content: data.message || data.response,
      };
    }
  } catch (error) {
    logger.error("Error in sendChatMessage:", error);
    throw error;
  }
}

/**
 * Fetch chat history for a specific user and track/race combination
 * @param {string} userId - The user's ID (phone number)
 * @param {object} context - Context containing track and race information
 * @returns {Promise<Array>} - Array of chat messages
 */
export async function fetchChatHistory(userId, context) {
  try {
    if (!userId) {
      logger.error("User ID is required to fetch chat history");
      return [];
    }

    // Format track and create contextMarker the same way we do for sending messages
    const formattedTrack = context?.track
      ? context.track.replace(/\s+/g, "-").toLowerCase()
      : "";
    const raceNumber = context?.race || "";
    const contextMarker =
      formattedTrack && raceNumber
        ? `${formattedTrack}-race-${raceNumber}`
        : "";

    if (!contextMarker) {
      logger.warn(
        "Context marker could not be created, cannot fetch specific chat history",
      );
      return [];
    }

    // logger.log("Fetching chat history with:", { userId, contextMarker, environment });

    // Create the session ID in the exact format seen in Firestore
    // Format: +61411619993-2025-04-17-albury-race-1
    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const sessionId = `${userId}-${formattedDate}-${formattedTrack}-race-${raceNumber}`;

    // logger.log("Using session ID:", sessionId);

    const response = await fetch(API_BASE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Origin: window.location.origin,
      },
      body: JSON.stringify({
        action: "getChatHistory",
        userId: userId,
        sessionId: sessionId,
        debug: true,
      }),
    });

    if (!response.ok) {
      logger.error(
        "Failed to fetch chat history:",
        response.status,
        response.statusText,
      );
      return [];
    }

    const data = await response.json();
    // logger.log("Raw chat history response:", data);

    // Check if we have data in the expected format
    if (!data || !data.status || data.status !== "success" || !data.data) {
      logger.warn("Invalid response format:", data);
      return [];
    }

    // Look specifically for a 'messages' collection
    if (!Array.isArray(data.data.messages) && !Array.isArray(data.data)) {
      // logger.warn("No messages array found in response");
      return [];
    }

    // Get the messages array (could be in data.data.messages or directly in data.data)
    const messages = Array.isArray(data.data.messages)
      ? data.data.messages
      : data.data;
    // logger.log("Found messages:", messages);

    // Transform the messages to match the frontend format
    const formattedMessages = messages
      .map((msg) => {
        // According to the Firestore structure, content is inside the data object
        const content = msg.data?.content || "";
        const type = msg.type || "";

        return {
          role: type === "human" ? "user" : "assistant",
          content: content,
        };
      })
      .filter((msg) => msg.content.trim() !== ""); // Filter out any empty messages

    // logger.log("Formatted messages:", formattedMessages);
    return formattedMessages;
  } catch (error) {
    logger.error("Error fetching chat history:", error);
    return [];
  }
}
